/**
 * IndustryPackPreviewModal
 *
 * Per-pack dry-run + install dialog opened from IndustryPacksSection. The
 * modal:
 *   1. Triggers a dry-run via templateService.seedTemplates (`dryRun: true`)
 *      as soon as it opens.
 *   2. Renders the per-entity-type preview table while the operator decides.
 *   3. On "Installer", commits the same request with `dryRun: false`.
 *
 * Dry-run is the contract between IndustryPackPreviewModal and the operator —
 * the modal never offers an install path that hasn't been previewed first.
 * Mobile breakpoints fold the dialog to fullScreen so the table stays
 * scannable on a phone (no ModalLayout primitive exists in this admin app
 * yet; matching the FlagOverridesDialog Dialog convention with fullScreen-on-
 * mobile is the pragmatic choice).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { templateService } from '../../services/templateService';
import {
  TemplateEntityType,
  SeedConflictStrategy,
  type SeedTemplatesResponse,
} from '../../services/templateService';
import type { IndustryPackListItem } from '../../services/industryPackService';

interface IndustryPackPreviewModalProps {
  open: boolean;
  onClose: () => void;
  pack: IndustryPackListItem;
  tenantId: string;
  tenantName: string;
  environment: string;
  /**
   * Called after a successful live install. Parents typically use this to
   * refetch the per-tenant audit query so the pack card's "Installert" state
   * updates.
   */
  onInstalled: () => void;
}

/**
 * Maps the pack's `contentCounts` keys to the wire-format TemplateEntityType
 * the seed endpoint expects. Only entries with a non-zero count are sent so
 * the seeder doesn't waste a transaction loading entity types the pack
 * doesn't carry.
 */
const buildEntityTypesForPack = (
  pack: IndustryPackListItem
): TemplateEntityType[] => {
  const types: TemplateEntityType[] = [];
  if (pack.contentCounts.checklistGroups > 0) {
    types.push(TemplateEntityType.CHECKLIST_GROUPS);
  }
  if (pack.contentCounts.folderTemplates > 0) {
    types.push(TemplateEntityType.FOLDER_TEMPLATES);
  }
  if (pack.contentCounts.activityTypes > 0) {
    types.push(TemplateEntityType.ACTIVITY_TYPES);
  }
  if (pack.contentCounts.consentBases > 0) {
    types.push(TemplateEntityType.CONSENT_BASES);
  }
  return types;
};

const ENTITY_TYPE_LABEL_NO: Record<string, string> = {
  [TemplateEntityType.CHECKLIST_GROUPS]: 'Sjekklistegrupper',
  [TemplateEntityType.FOLDER_TEMPLATES]: 'Mappemaler',
  [TemplateEntityType.ACTIVITY_TYPES]: 'Aktivitetstyper',
  [TemplateEntityType.CONSENT_BASES]: 'Samtykkegrunnlag',
};

export function IndustryPackPreviewModal({
  open,
  onClose,
  pack,
  tenantId,
  tenantName,
  environment,
  onInstalled,
}: IndustryPackPreviewModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const entityTypes = useMemo(() => buildEntityTypesForPack(pack), [pack]);

  const [dryRunResult, setDryRunResult] = useState<SeedTemplatesResponse | null>(
    null
  );
  const [dryRunError, setDryRunError] = useState<string | null>(null);

  const dryRunMutation = useMutation({
    mutationFn: templateService.seedTemplates,
    onSuccess: (data) => {
      setDryRunResult(data);
      setDryRunError(null);
    },
    onError: (error: Error) => {
      setDryRunResult(null);
      setDryRunError(error.message);
    },
  });

  const installMutation = useMutation({
    mutationFn: templateService.seedTemplates,
    onSuccess: (data) => {
      if (data.success) {
        enqueueSnackbar(`Pakka "${pack.displayName}" er installert`, {
          variant: 'success',
        });
        onInstalled();
      } else {
        enqueueSnackbar(
          `Pakka "${pack.displayName}" ble installert, men noen typer feilet`,
          { variant: 'warning' }
        );
        onInstalled();
      }
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Installasjon feilet: ${error.message}`, {
        variant: 'error',
      });
    },
  });

  // Trigger the dry-run exactly once per (open, pack) transition. When the
  // dialog reopens for the same pack we still re-fetch — the operator may
  // have committed something in another tab and we want the preview fresh.
  useEffect(() => {
    if (!open) {
      setDryRunResult(null);
      setDryRunError(null);
      return;
    }
    setDryRunResult(null);
    setDryRunError(null);
    dryRunMutation.mutate({
      tenantId,
      environment,
      entityTypes,
      conflictStrategy: SeedConflictStrategy.SKIP,
      source: `pack:${pack.key}`,
      dryRun: true,
    });
    // dryRunMutation is stable per react-query; safe to omit from deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pack.key, tenantId, environment]);

  const handleInstall = () => {
    installMutation.mutate({
      tenantId,
      environment,
      entityTypes,
      conflictStrategy: SeedConflictStrategy.SKIP,
      source: `pack:${pack.key}`,
      dryRun: false,
    });
  };

  const isDryRunLoading = dryRunMutation.isPending;
  const isInstalling = installMutation.isPending;
  const hasDryRunResult = !!dryRunResult;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="industry-pack-preview-modal-title"
    >
      <DialogTitle id="industry-pack-preview-modal-title">
        Forhåndsvis "{pack.displayName}" for {tenantName}
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Dette er en forhåndsvisning. Ingen endringer er gjort i
          tenant-databasen.
        </Alert>

        {isDryRunLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 4,
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Forhåndsviser…
            </Typography>
          </Box>
        )}

        {dryRunError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Forhåndsvisning feilet: {dryRunError}
          </Alert>
        )}

        {hasDryRunResult && dryRunResult && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Installerer pakka "{pack.displayName}" til tenant{' '}
              <strong>{tenantName}</strong> ({environment}).
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Vil opprette</TableCell>
                    <TableCell align="right">Vil oppdatere</TableCell>
                    <TableCell align="right">Vil hoppe over</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dryRunResult.results.map((result) => (
                    <TableRow key={result.entityType}>
                      <TableCell>
                        {ENTITY_TYPE_LABEL_NO[result.entityType] ??
                          result.entityType}
                      </TableCell>
                      <TableCell align="right">
                        {result.wouldCreate?.length ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {result.wouldUpdate?.length ?? 0}
                      </TableCell>
                      <TableCell align="right">
                        {result.wouldSkip?.length ?? 0}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isInstalling}
          sx={{ minHeight: 48 }}
        >
          Avbryt
        </Button>
        <Button
          variant="contained"
          onClick={handleInstall}
          disabled={isDryRunLoading || isInstalling || !hasDryRunResult}
          startIcon={
            isInstalling ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
          sx={{ minHeight: 48 }}
        >
          {isInstalling ? 'Installerer…' : 'Installer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
